import {
  DigitalHuman,
  PasscodeOverlay,
  EndSessionActions,
  feedbackStyles,
  Overlay,
  DynamicFormContext
} from 'uneeq-react-ui'
import { Image } from 'rebass'
import React, { useState, useRef, useContext } from 'react'
import { Box, Flex, Card, Text } from 'rebass'
import { ThemeProvider } from 'emotion-theming'

import { Textarea } from '@rebass/forms'
import { trackHandler, UneeqContext, useUneeqState } from 'uneeq-react-core'
import { Trans, useTranslation } from 'react-i18next'

import theme from '../theme'
import config from '../config'
import Home from './Home'
import assets from './assets'

import { default as backgroundVideo } from './assets/video/background-video-low.mp4'
import { default as backgroundImage } from './assets/img/we-background.jpg'

import { testState } from 'uneeq-react-core'
import SessionTimedOut from './SessionTimedOut'

const backgroundStyle = {
  label: 'wrapper',
  width: '100%',
  minHeight: '100%',
  backgroundSize: 'cover',
  backgroundPosition: 'right bottom',
  backgroundColor: '#c5e4c3',
  backgroundImage: [`url('/intesoweb-background.jpg')`],
  overflow: 'hidden',
  position: 'absolute'
}

// example CustomFeedback component that can be passed to DigitalHuman
// to customise the look and feel of this screen
const CustomFeedback = ({ restart, close, isOpen }: any) => {
  const [feedbackScore, setFeedbackScore] = useState<number | undefined>()
  const [recommendationScore, setRecommendationScore] = useState<
    number | undefined
  >()
  const [feedbackText, setFeedbackText] = useState('')
  const {
    dispatch,
    config,
    sendData,
    endSession,
    sessionId,
    uneeq
  } = useContext(UneeqContext)
  const { contactDetailsGiven } = useUneeqState()
  const { t } = useTranslation()

  const { formData } = useContext(DynamicFormContext)
  const hasFormData = Object.keys(formData).length > 0

  const giveFeedback = () => {
    if (feedbackScore || feedbackText) {
      // TODO: send feedback to API
      //sendData({ feedbackScore, feedbackText, recommendationScore })
      console.info('form sent', {
        rating: feedbackScore,
        comment: feedbackText,
        recommendationScore
      })
    }
    dispatch({ type: 'giveFeedback' })
  }

  const leaveChat = () => {
    giveFeedback()

    if (config.showEscalationForm) {
      if (!contactDetailsGiven) {
        dispatch({ type: 'openEscalationForm', payload: true })
      } else {
        endSession()
      }
    } else if (config.formsAPIURI !== '' && hasFormData) {
      window.open(`${config.formsFrontendURI}/?sid=${sessionId}`, '_blank')
      endSession()
    } else {
      endSession()
    }
  }

  if (!isOpen) return null

  return (
    <Overlay>
      <Card sx={feedbackStyles.modal}>
        <Text sx={feedbackStyles.title} variant="heading">
          {t('app:Feedback.areYouSure')}
        </Text>

        <EndSessionActions
          onConfirm={trackHandler(
            () => leaveChat(),
            'rate-session-leave-chat-btn'
          )}
          onDismiss={trackHandler(close, 'close-feedback-btn')}
          leaveChatLabel={
            config.formsAPIURI !== '' && hasFormData
              ? t('app:Feedback.leaveViewForm')
              : t('endSession.EndSessionActions.leaveChat')
          }
        />
      </Card>
    </Overlay>
  )
}

const App = () => {
  const { t } = useTranslation()

  const loadingTips = [
    {
      title: t('app:PermissionsGateway.loadingTip1'),
      showOnDesktop: true,
      showOnMobile: true
    }
  ]

  // For faster testing, skip straight to digitalHuman
  const [step, goTo] = useState(testState ? 'digitalHuman' : 'welcome')

  // const postInit = (uneeq: Uneeq) => {
  //   console.info('initialized')
  //   uneeq.sendTranscript('Example using initialized uneeq instance')
  // }

  // passcode is an overlay, not a step
  const [showPasscode, setShowPasscode] = useState(false)
  const tokenRef = useRef<string>()

  const start = () => {
    // Show passcode overlay or go straight to digitalHuman
    if (config.usePasscode) {
      setShowPasscode(true)
    } else {
      goTo('digitalHuman')
    }
  }

  const startWithToken = (token: string) => {
    tokenRef.current = token
    goTo('digitalHuman')
    setShowPasscode(false)
  }

  const restart = function() {
    window.location.reload()
  }

  const BGVideo = () => {
    return (
      <Flex
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%'
        }}
      >
        <video
          playsInline
          autoPlay
          muted
          loop
          poster="/intesoweb-background.jpg"
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'cover'
          }}
        >
          <source src={backgroundVideo} type="video/mp4" />
        </video>
      </Flex>
    )
  }

  return (
    <ThemeProvider theme={theme}>
      <Box
        sx={{
          ...backgroundStyle,
          overflow: step === 'welcome' ? 'visible' : 'hidden'
        }}
      >
        {config.imageBackground ? (
          <Image
            src={backgroundImage}
            sx={{
              position: 'absolute',
              width: '100%',
              height: '100%',
              objectFit: 'cover'
            }}
          />
        ) : (
          <BGVideo />
        )}

        {step === 'digitalHuman' ? (
          <DigitalHuman
            assets={assets}
            config={config}
            token={tokenRef.current}
            loadingTips={loadingTips}
            onTimedOut={() => {
              goTo('timed-out')
            }}
            // postInit={postInit}
            restart={trackHandler(restart, 'restart-btn')}
            CustomFeedback={CustomFeedback}
            onSessionEnded={() => goTo('restart')}
          />
        ) : step === 'timed-out' ? (
          <SessionTimedOut restart={trackHandler(restart, 'restart-btn')} />
        ) : (
          <Home startSession={trackHandler(start, 'start-btn')} />
        )}
        {showPasscode && (
          <PasscodeOverlay
            start={trackHandler(startWithToken, 'start-with-token-btn')}
            close={() => setShowPasscode(false)}
            config={config}
          />
        )}
      </Box>
    </ThemeProvider>
  )
}

export default App
